import { Dialog, Transition } from '@headlessui/react';
import { FC, HtmlHTMLAttributes, useEffect, useState } from 'react';
import tw from 'twin.macro';
import { XIcon } from '@heroicons/react/solid';
import IMobileVerificationProps from './interface';
import { useForm } from 'react-hook-form';
import Email from '../../../layout-a/forms/common/Inputs//Email';
import { handleDataTracker } from '../../tracker';
import MasterClassContainer from '../../container';
import UgGradGQL from '../../../../lib/graphql/constants/upGrad/gql';
import { RESEND_OTP, VERIFY_OTP } from '../../../../lib/graphql/constants/upGrad/lead.gql';
import { maskPhoneNumber } from '../../../../lib/common/common';

const CloseButton = tw.button`
  border-transparent
  outline-none
  z-50
`;

const DialogContainer = tw.div`
fixed
inset-0
bg-black
bg-opacity-25
transition-opacity
`;

const ButtonContainer = tw.div`
absolute
top-0
right-0
pt-4
pr-4
`;

const Title = tw.div`
md:text-center
text-left
text-xl
sm:text-[32px]
sm:leading-[41px]
font-bold
leading-[26px]
sm:mb-4
mb-6
relative
`;

const ContentContainer = tw.div`
sm:flex 
sm:justify-center 
sm:items-center `;

const Content = tw.div`
  w-full sm:max-w-[447px] text-[#333333] font-regular text-sm leading-[21px] sm:text-base sm:leading-6 mb-10 md:text-center text-left
`;

const InfoContainer = tw.div`
md:py-[33px]
pb-6
`;

const Popup = tw.div`
relative
inline-block
bg-white
rounded-t-[3px] 
md:rounded-xl
px-4
pt-3
pb-4
text-left
overflow-hidden
shadow-xl
transform
transition-all
sm:my-8   sm:p-[15px]
`;

const PopupContainer = tw.div`
flex
md:items-center
items-end
justify-center
min-h-screen
text-center
`;
const Overlay = tw.div`
      fixed inset-0 bg-black bg-opacity-50 transition-opacity
  `;

const OtpContainer = tw.div`w-12 h-12`;

const VerifyButtonContainer = tw.div`
flex 
justify-center 
items-center 
`;

const VerifyButton = tw.button`
w-full 
sm:w-[164px]  
text-white 
py-4 
text-center 
font-semibold 
md:font-bold 
md:text-xl 
md:leading-6 
text-sm 
leading-[19px] 
rounded-[3px] 
md:rounded`;

const ContactUsContent = tw.p` 
font-regular 
text-sm 
leading-[21px] 
text-[#666666]
text-center`;

const FormContainer = tw.form`
    leading-[22px] text-[#444] text-sm
`;

const MobileVerificationPopUp: FC<IMobileVerificationProps> = ({
  title,
  isOpen,
  setOpen,
  leadData,
  sectionName,
  setShowSuccessPopup,
  setShowBookYouSeatPopUp,
  webinarId,
  webinarDate,
  webinarEndDate,
  zoomLink,
  instructor,
  classType,
}) => {
  const [counter, setCounter] = useState(30);

  const [showResendOTPPopup, setShowResendOTPPopup] = useState(false);
  const [otp, setOtp] = useState([]);
  const [enableOtpButton, setEnableOtpButton] = useState(true);
  const [otpStatus, setOtpStatus] = useState(false);
  const [otpSendStatus, setOtpSendStatus] = useState(false);

  useEffect(() => {
    let timer = counter > 0 && isOpen && setTimeout(() => setCounter(counter - 1), 1000);
    return () => {
      clearInterval(timer);
    };
  }, [counter]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: 'onTouched',
  });

  const inputProps = { register, errors, isSubmitted, setValue, getValues };

  const { leadIdentifier, phone, program, email } = leadData;
  let webinarStartTime = webinarDate;
  let webinarEndTime = webinarEndDate;

  // validateOtp
  const validateOtp = async () => {
    const userSubmittedOTP = otp.join('');
    const response = await UgGradGQL({
      query: VERIFY_OTP,
      variables: {
        input: {
          email,
          otp: userSubmittedOTP,
          phone,
          program,
          webinarId,
          zoomLink,
          webinarStartTime,
          webinarEndTime,
          instructor,
          classType,
        },
      },
      queryName: 'verifyOTP',
      queryType: 'mutation',
      context: 'verifyOTP',
    });

    if (response.data) {
      setOtpStatus(false);
      close();
      setShowSuccessPopup(true);
      setOpen(false);
      handleDataTracker(`${sectionName}-otp`, '', program, leadData);
    } else {
      setOtpStatus(true);
      setOtpSendStatus(false);
    }
  };

  // close
  const close = () => {
    setOtpStatus(false);
    setOpen(false);
    document.body.style.overflow = 'auto';
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
  }, [isOpen]);

  // inputOnChange
  const inputOnChange = (e, i) => {
    const value = e.target.value;

    if (!new RegExp(/^\d+$/).test(value)) {
      otp[i] = '';
    } else {
      otp[i] = value;
    }

    if (e.nativeEvent.inputType === 'deleteContentBackward') {
      const nextSibling: HTMLElement = document.querySelector(`input[name=otp-${parseInt(i) - 1}]`);

      if (nextSibling !== null) {
        if (i != 0) {
          nextSibling.focus();
        }
      }
    } else {
      const nextSibling: HTMLElement = document.querySelector(`input[name=otp-${parseInt(i) + 1}]`);

      if (nextSibling !== null) {
        if (i != 3) {
          nextSibling.focus();
        }
      }
    }

    setOtp(otp);

    let otpFilter = otp.filter(function (el) {
      return el;
    });
    if (otpFilter.length == 4) {
      setEnableOtpButton(false);
    } else {
      setEnableOtpButton(true);
    }
  };

  // sendOtpMail
  const sendOtpMail = async () => {
    setCounter(30);

    const response = await UgGradGQL({
      query: RESEND_OTP,
      variables: { input: { email, phone } },
      queryName: 'resendOTP',
      queryType: 'mutation',
      context: 'resendOTP',
    });

    if (response.data) {
      setOtpSendStatus(true);
      setOtpStatus(false);
    }
  };
  return (
    <>
      <div className="absolute">
        <Transition.Root show={isOpen}>
          <div className="fixed z-[200] inset-0 overflow-y-auto">
            <MasterClassContainer>
              <PopupContainer className="flex md:items-center items-end justify-center text-center sm:block sm:p-0">
                <Transition.Child
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  {/* <Dialog.Overlay> */}
                  <Overlay />
                  {/* </Dialog.Overlay> */}
                </Transition.Child>

                <Transition.Child
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Popup className={`${showResendOTPPopup ? 'sm:w-[576px]' : 'sm:w-[697px]'} -mb-[6px] md:mb-0`}>
                    <ButtonContainer className="z-[250]">
                      <CloseButton type="button" onClick={close}>
                        <XIcon className="w-4 hover:text-[#585858]" />
                      </CloseButton>
                    </ButtonContainer>
                    <InfoContainer>
                      {showResendOTPPopup ? (
                        <>
                          <div className=" mb-4 text-left md:text-center text-xl sm:text-[32px] sm:leading-[41px] font-bold leading-[26px]">
                            {title}
                          </div>
                          <ContentContainer>
                            <div className="text-[#333333] font-regular text-sm leading-[21px] sm:text-base sm:leading-6 mb-10 sm:mb-[60px]">
                              Please enter the phone number you wish to receive the OTP in
                            </div>
                          </ContentContainer>
                          <ContentContainer className="mb-[23px] sm:mb-4">
                            <FormContainer>
                              <div className="w-full sm:w-[286px]">
                                {/* Email & phone number */}
                                <Email {...inputProps} asterisk={true} fullWidth={true} />
                              </div>
                            </FormContainer>
                          </ContentContainer>

                          <VerifyButtonContainer className="mb-[38px] sm:mb-8">
                            <button
                              className=" w-full sm:w-[129px] font-semibold sm:font-bold text-sm sm:text-base leading-[19px] bg-[#db4144] text-white py-4 rounded-[3px] md:rounded"
                              onClick={() => {
                                setShowResendOTPPopup(false);
                              }}
                            >
                              Get OTP
                            </button>
                          </VerifyButtonContainer>
                        </>
                      ) : (
                        <>
                          <div className="hidden md:flex  justify-center items-center relative">
                            <button
                              onClick={() => {
                                setShowBookYouSeatPopUp(true);
                                setOpen(false);
                              }}
                              className="absolute left-[120px] top-2 z-[200] cursor-pointer"
                            >
                              <i className="back-icon bg-spriteImg w-8 h-8"></i>
                            </button>
                            <Title className="block md:hidden">{title}</Title>
                          </div>
                          <Title>{title}</Title>
                          <ContentContainer>
                            <Content>
                              Please verify your Mobile number to receive your Webinar Joining link from upGrad. By the
                              way, we have sent a code to your Mobile number {maskPhoneNumber(leadData.phone)}
                              {/* <span
                                onClick={() => {
                                  setShowResendOTPPopup(true);
                                }}
                                className="text-[#107BD4] underline"
                              >
                                Click here
                              </span> */}
                            </Content>
                          </ContentContainer>

                          <div className="flex flex-row items-center justify-between mx-auto w-full max-w-xs">
                            <OtpContainer>
                              <input
                                className="w-full h-full flex flex-col items-center justify-center text-center px-2 outline-none rounded border border-gray-200 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
                                type="number"
                                name="otp-0"
                                onChange={(e) => inputOnChange(e, 0)}
                                maxLength={1}
                              />
                            </OtpContainer>
                            <OtpContainer>
                              <input
                                className="w-full h-full flex flex-col items-center justify-center text-center px-2 outline-none rounded border border-gray-200 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
                                type="number"
                                name="otp-1"
                                onChange={(e) => inputOnChange(e, 1)}
                                maxLength={1}
                              />
                            </OtpContainer>
                            <OtpContainer>
                              <input
                                className="w-full h-full flex flex-col items-center justify-center text-center px-2 outline-none rounded border border-gray-200 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
                                type="number"
                                name="otp-2"
                                onChange={(e) => inputOnChange(e, 2)}
                                maxLength={1}
                              />
                            </OtpContainer>
                            <OtpContainer>
                              <input
                                className="w-full h-full flex flex-col items-center justify-center text-center px-2 outline-none rounded border border-gray-200 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
                                type="number"
                                name="otp-3"
                                onChange={(e) => inputOnChange(e, 3)}
                                maxLength={1}
                              />
                            </OtpContainer>
                          </div>

                          {otpStatus && (
                            <div className="mx-auto w-full max-w-xs flex justify-center items-center mt-[6px]">
                              <span className="text-red-500 md:text-sm text-xs leading-4">OTP Verification Failed</span>
                            </div>
                          )}

                          {otpSendStatus && (
                            <div className="mx-auto w-full max-w-xs flex justify-center items-center mt-[6px]">
                              <span className="text-red-500 md:text-sm text-xs leading-4">OTP sent successfully.</span>
                            </div>
                          )}

                          <div className=" mt-4 sm:mt-8  text-center mb-10 sm:mb-12 font-semibold text-sm leading-[19px] sm:text-base sm:leading-[21px] ">
                            {counter == 0 ? (
                              <span onClick={sendOtpMail}>
                                {' '}
                                <label className="text-blue-500 cursor-pointer">Resend OTP</label>
                              </span>
                            ) : (
                              <>
                                Didn’t recieve OTP? Resend in <span className="text-[#666666]">{counter}</span> sec
                              </>
                            )}
                          </div>
                          <VerifyButtonContainer className="mb-6">
                            <VerifyButton
                              className={`${enableOtpButton ? 'bg-[#db4144]/70 cursor-not-allowed ' : 'bg-[#db4144]'}`}
                              onClick={() => {
                                validateOtp();
                              }}
                              disabled={enableOtpButton}
                              name="verifyOTP"
                            >
                              Verify OTP
                            </VerifyButton>
                          </VerifyButtonContainer>
                        </>
                      )}
                      <ContactUsContent>
                        Have any further questions &nbsp;
                        <a
                          className=" font-semibold underline"
                          href="mailto:bootcampsupport@upgrad.com"
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Contact Us
                        </a>
                      </ContactUsContent>
                    </InfoContainer>
                  </Popup>
                </Transition.Child>
              </PopupContainer>
            </MasterClassContainer>
          </div>
        </Transition.Root>
      </div>
    </>
  );
};

export default MobileVerificationPopUp;
